import { Alert, Button, Card, Empty, Table, Tag, Tooltip, Typography } from 'antd';
import {
	CheckCircleTwoTone,
	PaperClipOutlined,
	WarningTwoTone,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { useGetOrderLineItemTicketsQuery } from '../../../redux/api/order-line-item';
import type {
	ShopFloorTicket,
	ShopFloorTicketTimelineEntry,
} from '../../../interfaces/ShopFloorTicket.interface';

const { Text } = Typography;

const PRIORITY_COLORS: Record<string, string> = {
	critical: 'red',
	high: 'volcano',
	normal: 'blue',
	low: 'default',
};

/** shop-floor's snake_case vocabulary, rendered as words. */
const humanize = (value?: string) =>
	value ? value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '';

const moment = (value?: string | null) =>
	value ? dayjs(value).format('MMM D, YYYY HH:mm') : '—';

const eventLabel = (entry: ShopFloorTicketTimelineEntry) => {
	if (entry.from_status) {
		return `${humanize(entry.from_status)} → ${humanize(entry.to_status)}`;
	}
	const assignedTo = entry.payload?.assigned_to;
	if (entry.event_type === 'assigned' && typeof assignedTo === 'string') {
		return `Assigned to ${assignedTo}`;
	}
	return humanize(entry.event_type);
};

/**
 * One thing that happened, in the words of what happened.
 *
 * Attachments are named rather than shown. The photos live in the Shop Floor
 * App behind URLs that expire, so anything embedded here would be a broken
 * image in a week; the reader gets told one exists and sent to the live
 * ticket for it.
 */
const TimelineEntry = ({ entry }: { entry: ShopFloorTicketTimelineEntry }) => (
	<div className="flex gap-3 py-1">
		<span className="w-40 shrink-0 text-xs text-neutral-400">
			{moment(entry.at)}
		</span>
		<span className="min-w-0 flex-1 text-sm">
			{entry.kind === 'status' ? (
				<Text type="secondary">
					{eventLabel(entry)}
					{entry.actor ? ` · ${entry.actor}` : ''}
				</Text>
			) : (
				<>
					{entry.author && <Text strong>{entry.author}: </Text>}
					<span className="whitespace-pre-wrap">{entry.text}</span>
					{entry.attachment_kind && (
						<Text type="secondary" className="ml-2 whitespace-nowrap">
							<PaperClipOutlined />{' '}
							{humanize(entry.attachment_kind) || 'File'} attached
							{entry.attachment_name ? ` — ${entry.attachment_name}` : ''}
						</Text>
					)}
				</>
			)}
		</span>
	</div>
);

const TicketTimeline = ({ ticket }: { ticket: ShopFloorTicket }) => (
	<div className="pl-2">
		{ticket.description && (
			<p className="mb-2 whitespace-pre-wrap text-sm">{ticket.description}</p>
		)}

		{ticket.timeline.length ? (
			ticket.timeline.map((entry) => (
				<TimelineEntry key={`${entry.kind}-${entry.id}`} entry={entry} />
			))
		) : (
			<Text type="secondary" className="text-sm">
				Nothing has been logged against this ticket yet.
			</Text>
		)}

		{ticket.resolution_summary && (
			<p className="mb-0 mt-2 text-sm">
				<Text strong>Resolution: </Text>
				<span className="whitespace-pre-wrap">{ticket.resolution_summary}</span>
			</p>
		)}

		{ticket.source_url && (
			<p className="mb-0 mt-3 text-xs">
				<a href={ticket.source_url} target="_blank" rel="noreferrer">
					Open in the Shop Floor App
				</a>{' '}
				<Text type="secondary">for photos and to reply.</Text>
			</p>
		)}
	</div>
);

/**
 * The shop-floor conversation about this job.
 *
 * Read-only on purpose. A ticket is worked on the floor -- replied to, routed,
 * closed -- and this is the ERP's mirror of it, so an edit made here would be
 * overwritten by the next thing that happened over there. The value is that
 * "why did this job sit in Machining for eighty days" now has an answer on the
 * job itself.
 *
 * Collapsed rows expanding to a timeline, rather than a feed: most jobs have
 * no tickets and the ones that do usually have one, so the summary is what is
 * wanted and the detail is what is occasionally wanted. It is also the idiom
 * already on this page, in Material Reservations.
 *
 * Renders nothing at all when a job has no tickets. A permanent empty card on
 * every job page would be a cost paid by everyone for a state that is the
 * normal one.
 */
const OrderLineItemTickets = ({ lineItemId }: { lineItemId: string }) => {
	const { data, isError, refetch } = useGetOrderLineItemTicketsQuery(
		lineItemId,
		{
			skip: !lineItemId,
			pollingInterval: 30_000,
			refetchOnFocus: true,
			refetchOnReconnect: true,
		}
	);

	if (isError) {
		return (
			<Card className="mt-4" title="Shop Floor Tickets">
				<Alert
					type="warning"
					showIcon
					message="Ticket information could not be refreshed."
					action={<Button onClick={() => refetch()}>Retry</Button>}
				/>
			</Card>
		);
	}

	// Covers both "loaded and empty" and "still loading the first time" --
	// `data` is undefined until the first response lands, so waiting on
	// `data?.length` rather than `!isFetching` keeps the ~90% of jobs with no
	// tickets from flashing a loading card before collapsing away.
	if (!data?.length) return null;

	const tickets = data;
	const openCount = tickets.filter((ticket) => ticket.is_open).length;

	return (
		<Card
			className="mt-4"
			title={
				<div className="flex items-center gap-2">
					<span>Shop Floor Tickets</span>
					{openCount > 0 ? (
						<Tag color="warning">
							{openCount} open
						</Tag>
					) : (
						<Tag color="success">All resolved</Tag>
					)}
				</div>
			}
		>
			<Table<ShopFloorTicket>
				size="small"
				rowKey="id"
				dataSource={tickets}
				pagination={tickets.length > 5 ? { pageSize: 5 } : false}
				locale={{
					emptyText: (
						<Empty
							image={Empty.PRESENTED_IMAGE_SIMPLE}
							description="No shop floor tickets"
						/>
					),
				}}
				expandable={{
					expandedRowRender: (ticket) => <TicketTimeline ticket={ticket} />,
					// The first ticket on a job is almost always the one being
					// looked for, so it starts open rather than making the reader
					// click to find out whether it was worth clicking.
					defaultExpandedRowKeys: tickets.length ? [tickets[0].id] : [],
				}}
				columns={[
					{
						title: 'Ticket',
						dataIndex: 'uid',
						width: 110,
						render: (uid: string, ticket) => (
							<span className="whitespace-nowrap">
								{ticket.is_open ? (
									<WarningTwoTone twoToneColor="#faad14" />
								) : (
									<CheckCircleTwoTone twoToneColor="#52c41a" />
								)}{' '}
								<Text strong>{uid || '—'}</Text>
							</span>
						),
					},
					{
						title: 'Status',
						dataIndex: 'status',
						width: 140,
						render: (status: string) => humanize(status) || '—',
					},
					{
						title: 'Priority',
						dataIndex: 'priority',
						width: 100,
						render: (priority: string) =>
							priority ? (
								<Tag color={PRIORITY_COLORS[priority] ?? 'default'}>
									{humanize(priority)}
								</Tag>
							) : (
								'—'
							),
					},
					{
						title: 'Route',
						dataIndex: 'route',
						width: 100,
						render: (route: string) => humanize(route) || '—',
					},
					{
						title: 'Machine',
						dataIndex: 'machine_name',
						width: 110,
						render: (machine: string) => machine || '—',
					},
					{
						title: 'Opened',
						dataIndex: 'opened_at',
						width: 160,
						render: (value: string | null, ticket) => (
							<Tooltip
								title={
									ticket.reported_by_name
										? `Reported by ${ticket.reported_by_name}`
										: undefined
								}
							>
								{moment(value)}
							</Tooltip>
						),
					},
					{
						title: 'Resolved',
						dataIndex: 'closed_at',
						width: 160,
						render: (value: string | null) => moment(value),
					},
					{
						title: 'Replies',
						dataIndex: 'reply_count',
						width: 80,
						align: 'right' as const,
						render: (count: number) => count || '—',
					},
				]}
			/>
		</Card>
	);
};

export default OrderLineItemTickets;
